import { useCallback, useMemo } from 'react';
import { invalidateOptimizerSummariesCache } from './optimizerSummariesCache';

export const RISK_INVALIDATION_SCOPES = Object.freeze({
    WATCHLIST: 'watchlist',
    MATRIX_SCOPE: 'matrix_scope',
    ACTUAL_PORTFOLIO: 'actual_portfolio',
    BETA_SOURCE: 'beta_source',
    IDIO_VOL_SOURCE: 'idio_vol_source',
    SELECTED_OVERVIEW: 'selected_overview',
    OPTIMIZER_SUMMARIES: 'optimizer_summaries',
});

export const RISK_INVALIDATION_CAUSES = Object.freeze({
    WATCHLIST_MEMBERSHIP_CHANGED: 'watchlist_membership_changed',
    ACTUAL_PORTFOLIO_CHANGED: 'actual_portfolio_changed',
    PRICE_WATERMARK_CHANGED: 'price_watermark_changed',
    FACTOR_CONTEXT_CHANGED: 'factor_context_changed',
    BETA_SOURCE_CHANGED: 'beta_source_changed',
    IDIO_VOL_SOURCE_CHANGED: 'idio_vol_source_changed',
    POS_SIZING_RESULT_CHANGED: 'pos_sizing_result_changed',
    RISK_CONFIG_CHANGED: 'risk_config_changed',
});

export const INVALIDATION_SCOPES_BY_CAUSE = Object.freeze({
    [RISK_INVALIDATION_CAUSES.WATCHLIST_MEMBERSHIP_CHANGED]: [
        RISK_INVALIDATION_SCOPES.WATCHLIST,
        RISK_INVALIDATION_SCOPES.MATRIX_SCOPE,
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
        RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.ACTUAL_PORTFOLIO_CHANGED]: [
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.PRICE_WATERMARK_CHANGED]: [
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
        RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.FACTOR_CONTEXT_CHANGED]: [
        RISK_INVALIDATION_SCOPES.MATRIX_SCOPE,
        RISK_INVALIDATION_SCOPES.WATCHLIST,
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
        RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.BETA_SOURCE_CHANGED]: [
        RISK_INVALIDATION_SCOPES.BETA_SOURCE,
        RISK_INVALIDATION_SCOPES.WATCHLIST,
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
        RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.IDIO_VOL_SOURCE_CHANGED]: [
        RISK_INVALIDATION_SCOPES.IDIO_VOL_SOURCE,
        RISK_INVALIDATION_SCOPES.MATRIX_SCOPE,
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
        RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.POS_SIZING_RESULT_CHANGED]: [
        RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW,
        RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES,
    ],
    [RISK_INVALIDATION_CAUSES.RISK_CONFIG_CHANGED]: [
        RISK_INVALIDATION_SCOPES.ACTUAL_PORTFOLIO,
    ],
});

const normalizeList = (values = []) => {
    if (!Array.isArray(values)) {
        return values == null ? [] : [values];
    }
    return values;
};

const uniqueStrings = (values = []) => {
    const seen = new Set();
    const result = [];
    normalizeList(values).forEach((value) => {
        if (value == null) {
            return;
        }
        const normalized = String(value).trim();
        if (!normalized || seen.has(normalized)) {
            return;
        }
        seen.add(normalized);
        result.push(normalized);
    });
    return result;
};

const requireFinancialPublicationTransaction = ({
    operation,
    stagePublicationState,
    generation,
    resourceScopeKey,
}) => {
    if (
        typeof stagePublicationState !== 'function'
        || !Number.isInteger(generation)
        || generation < 0
        || typeof resourceScopeKey !== 'string'
        || resourceScopeKey.trim() === ''
    ) {
        throw new Error(
            `${operation} requires an explicit publication transaction, generation, and scope.`,
        );
    }
};

export function getInvalidationScopes(cause) {
    const normalizedCause = String(cause || '').trim();
    if (!Object.prototype.hasOwnProperty.call(INVALIDATION_SCOPES_BY_CAUSE, normalizedCause)) {
        throw new Error(`Unknown risk invalidation cause: ${normalizedCause || '(missing)'}`);
    }
    return INVALIDATION_SCOPES_BY_CAUSE[normalizedCause];
}

export function normalizeRiskInvalidation({
    cause,
    reason = 'unspecified',
} = {}) {
    const normalizedCause = String(cause || '').trim();
    return {
        cause: normalizedCause,
        reason,
        scopes: [...getInvalidationScopes(normalizedCause)],
    };
}

export function buildRefreshRiskSurfaceLoadOptions({
    reason = 'init',
    requireFreshMatrices = false,
    includeSelectedOverview = false,
    betaSource,
    idioVolSource,
    matrixScopeVersion,
    watchlistIdentityScopeVersion,
    latestFactorDataVersionKey,
    factorVendorOverride,
    factorDataModelOverride,
    factorSnapshotDateOverride,
    positionsVersion,
    includeOptimizerSummaries = true,
    deferSecondary = false,
    preserveExistingOnMatrixFailure = false,
    securityOperationId = null,
    securityOperationType = null,
    expectedWatchlistRevisionId = null,
    expectedWatchlistRevisionSeq = null,
    exclusiveCoreLoadOwner = null,
    publicationVersionTransition = null,
    preparePublication = false,
    deferSelectedOverview = false,
    stagePresentationState = null,
} = {}) {
    const loadOptions = {
        reason,
    };
    if (includeSelectedOverview) {
        loadOptions.includeOptimized = true;
    }
    if (requireFreshMatrices) {
        loadOptions.forceMatricesReload = true;
    }
    if (idioVolSource != null) {
        loadOptions.idioVolSource = idioVolSource;
    }
    if (betaSource != null) {
        loadOptions.betaSource = betaSource;
    }
    if (matrixScopeVersion != null) {
        loadOptions.matrixScopeVersion = matrixScopeVersion;
    }
    if (watchlistIdentityScopeVersion != null) {
        loadOptions.watchlistIdentityScopeVersion = watchlistIdentityScopeVersion;
    }
    if (latestFactorDataVersionKey != null) {
        loadOptions.latestFactorDataVersionKey = latestFactorDataVersionKey;
    }
    if (factorVendorOverride != null) {
        loadOptions.factorVendorOverride = factorVendorOverride;
    }
    if (factorDataModelOverride != null) {
        loadOptions.factorDataModelOverride = factorDataModelOverride;
    }
    if (factorSnapshotDateOverride !== undefined) {
        loadOptions.factorSnapshotDateOverride = factorSnapshotDateOverride;
    }
    if (positionsVersion != null) {
        loadOptions.positionsVersion = positionsVersion;
    }
    if (includeOptimizerSummaries !== true) {
        loadOptions.includeOptimizerSummaries = includeOptimizerSummaries;
    }
    if (deferSecondary) {
        loadOptions.deferSecondary = true;
    }
    if (preserveExistingOnMatrixFailure) {
        loadOptions.preserveExistingOnMatrixFailure = true;
    }
    if (securityOperationId != null) {
        loadOptions.securityOperationId = securityOperationId;
    }
    if (securityOperationType != null) {
        loadOptions.securityOperationType = securityOperationType;
    }
    if (expectedWatchlistRevisionId != null) {
        loadOptions.expectedWatchlistRevisionId = expectedWatchlistRevisionId;
    }
    if (expectedWatchlistRevisionSeq != null) {
        loadOptions.expectedWatchlistRevisionSeq = expectedWatchlistRevisionSeq;
    }
    if (exclusiveCoreLoadOwner != null) {
        loadOptions.exclusiveCoreLoadOwner = exclusiveCoreLoadOwner;
    }
    if (publicationVersionTransition != null) {
        loadOptions.publicationVersionTransition = publicationVersionTransition;
    }
    if (preparePublication) {
        loadOptions.preparePublication = true;
        loadOptions.deferSelectedOverview = deferSelectedOverview;
        loadOptions.stagePresentationState = stagePresentationState;
    }
    return loadOptions;
}

export default function useRiskOrchestrationCommands({
    userUid,
    loadCoreDataRef,
    fetchOptimizedRiskMetrics,
    fetchGroupRiskMetrics,
    refreshActualRiskLensForIdioSource,
    fetchPosSizingData,
    fetchPosSizingSyncStatus,
    fetchOptimizerPortfolioSummaries,
    matrixContextKeyRef,
    invalidateSelectedOverview,
    refreshSelectedOverview,
} = {}) {
    const applyInvalidationEffects = useCallback((invalidation, { preserveVisibleState = false } = {}) => {
        const scopes = new Set(invalidation.scopes);
        if (scopes.has(RISK_INVALIDATION_SCOPES.MATRIX_SCOPE) && matrixContextKeyRef) {
            matrixContextKeyRef.current = '';
        }
        if (scopes.has(RISK_INVALIDATION_SCOPES.OPTIMIZER_SUMMARIES)) {
            invalidateOptimizerSummariesCache(userUid);
        }
        if (!preserveVisibleState && scopes.has(RISK_INVALIDATION_SCOPES.SELECTED_OVERVIEW)) {
            invalidateSelectedOverview?.();
        }
    }, [
        invalidateSelectedOverview,
        matrixContextKeyRef,
        userUid,
    ]);

    const invalidateRiskSurface = useCallback((options = {}) => {
        const invalidation = normalizeRiskInvalidation(options);
        applyInvalidationEffects(invalidation, options);
        return invalidation;
    }, [
        applyInvalidationEffects,
    ]);

    const invalidateRiskSurfaces = useCallback((optionsList = [], presentationOptions = {}) => {
        if (!Array.isArray(optionsList) || optionsList.length === 0) {
            throw new Error('invalidateRiskSurfaces requires at least one invalidation cause.');
        }
        // Normalize every cause before applying anything so one unknown cause
        // cannot leave the client partially invalidated.
        const invalidations = optionsList.map((options) => normalizeRiskInvalidation(options));
        applyInvalidationEffects({
            scopes: uniqueStrings(invalidations.flatMap(({ scopes }) => scopes)),
        }, presentationOptions);
        return invalidations;
    }, [
        applyInvalidationEffects,
    ]);

    const refreshRiskSurface = useCallback(async (options = {}) => {
        const reason = options.reason ?? 'init';
        const loadOptions = buildRefreshRiskSurfaceLoadOptions({
            reason,
            ...options,
        });
        if (typeof loadCoreDataRef?.current !== 'function') {
            throw new Error('refreshRiskSurface requires loadCoreDataRef.current');
        }
        return await loadCoreDataRef.current(loadOptions);
    }, [loadCoreDataRef]);

    const refreshPortfolioRisk = useCallback(async ({
        portfolioId,
        targetKey,
        riskContextOverride = null,
        isCurrentRequest = null,
        stagePublicationState,
        generation,
        resourceScopeKey,
    } = {}) => {
        if (portfolioId == null) {
            return null;
        }

        const isSelectedTarget = targetKey === 'opt' || targetKey === 'pos';
        if (!isSelectedTarget) {
            throw new Error('refreshPortfolioRisk requires targetKey opt or pos.');
        }
        requireFinancialPublicationTransaction({
            operation: 'refreshPortfolioRisk',
            stagePublicationState,
            generation,
            resourceScopeKey,
        });
        if (typeof fetchOptimizedRiskMetrics !== 'function') {
            throw new Error('refreshPortfolioRisk requires fetchOptimizedRiskMetrics');
        }
        if (typeof fetchGroupRiskMetrics !== 'function') {
            throw new Error('refreshPortfolioRisk requires fetchGroupRiskMetrics');
        }
        await fetchOptimizedRiskMetrics(
            portfolioId,
            null,
            riskContextOverride,
            {
                isCurrentRequest,
                stagePublicationState,
                generation,
                targetKey,
                resourceScopeKey,
            },
        );
        await fetchGroupRiskMetrics({
            portfolioId,
            targetKey,
            riskContextOverride,
            isCurrentRequest,
            stagePublicationState,
            generation,
            resourceScopeKey,
        });
    }, [
        fetchOptimizedRiskMetrics,
        fetchGroupRiskMetrics,
    ]);

    const refreshActualRiskLens = useCallback(async ({
        idioVolSource,
        riskContextOverride = null,
        isCurrentRequest = null,
        stagePublicationState,
        generation,
        resourceScopeKey,
    } = {}) => {
        requireFinancialPublicationTransaction({
            operation: 'refreshActualRiskLens',
            stagePublicationState,
            generation,
            resourceScopeKey,
        });
        if (typeof refreshActualRiskLensForIdioSource !== 'function') {
            throw new Error(
                'refreshActualRiskLens requires refreshActualRiskLensForIdioSource',
            );
        }
        await refreshActualRiskLensForIdioSource({
            idioVolSource,
            riskContextOverride,
            isCurrentRequest,
            stagePublicationState,
            generation,
            resourceScopeKey,
        });
    }, [refreshActualRiskLensForIdioSource]);

    const refreshPosSizingContext = useCallback(async ({
        reason = 'pos_sizing',
        idioVolSource,
        matricesOverride = null,
        fetchData = false,
        persistPortfolio = false,
        requestId = null,
        includeSyncStatus = true,
        refreshOptimizerSummaries = false,
        isCurrentRequest = null,
        stagePublicationState = null,
        preparedTarget = null,
    } = {}) => {
        void reason;
        let result = null;
        let didHydrate = false;
        if (fetchData) {
            didHydrate = true;
            result = await fetchPosSizingData?.({
                persistPortfolio,
                matricesOverride,
                idioVolSource,
                requestId,
                ...(stagePublicationState ? { stagePublicationState } : {}),
                ...(preparedTarget ? { preparedTarget } : {}),
                ...(isCurrentRequest ? { isCurrentRequest } : {}),
            });
        }

        const syncStatusOptions = idioVolSource == null ? {} : { idioVolSource };
        let syncStatus = null;
        if (didHydrate) {
            syncStatus = result?.pos_sizing_sync_status ?? null;
        } else if (includeSyncStatus) {
            syncStatus = await fetchPosSizingSyncStatus?.(syncStatusOptions);
        }
        const optimizerSummaries = refreshOptimizerSummaries
            ? await fetchOptimizerPortfolioSummaries?.({ force: true,
                ...(stagePublicationState ? { stagePublicationState } : {}),
                ...(preparedTarget ? { preparedTarget } : {}),
                ...(isCurrentRequest ? { isCurrentRequest } : {}) })
            : null;
        return {
            result,
            syncStatus,
            optimizerSummaries,
        };
    }, [
        fetchOptimizerPortfolioSummaries,
        fetchPosSizingData,
        fetchPosSizingSyncStatus,
    ]);

    const refreshOptimizerSummaries = useCallback(async (options = {}) => (
        await fetchOptimizerPortfolioSummaries?.({ force: true, ...options })
    ), [fetchOptimizerPortfolioSummaries]);

    return useMemo(() => ({
        invalidateRiskSurface,
        invalidateRiskSurfaces,
        refreshRiskSurface,
        refreshPortfolioRisk,
        refreshActualRiskLens,
        refreshPosSizingContext,
        refreshOptimizerSummaries,
        // Owned by useOverviewSelectionRefresh; workflows call it when done.
        refreshSelectedOverview,
    }), [
        invalidateRiskSurface,
        invalidateRiskSurfaces,
        refreshActualRiskLens,
        refreshPortfolioRisk,
        refreshOptimizerSummaries,
        refreshPosSizingContext,
        refreshRiskSurface,
        refreshSelectedOverview,
    ]);
}
